/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, useTheme } from '@mui/material';

import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { NamedIconsType } from '@europrocurement/l2d-icons';
import { ModelItem, useSociete } from '@europrocurement/l2d-domain';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { getDataThunkType } from '@europrocurement/l2d-redux-utils';

import GenericSearchBar from '../../Societes/components/GenericSearchBar';
import { useSearchBar } from '../../Societes/providers/SearchBarProvider';

type SelectableItem = { id: string | number };

export type RoutedListingProps = {
    listingTitle: string;
    createButton: {
        label: string;
        icon: NamedIconsType;
        onClick: () => void;
    };
    listingColumns: ColumnDatatable<unknown>[];
    model: ModelItem<unknown>;
    searchBarType?: string;
    filters?: FiltersDatatableList;
    filtersControl?: boolean;
    /** Appelé lors du clic sur une ligne : navigation vers la fiche de l'objet. */
    onRowClick: (item: SelectableItem) => void;
};

/**
 * Listing avec SearchBar unifiée dont chaque ligne navigue vers une fiche dédiée
 * (mécanique de fiche par objet sur route, en remplacement des modales).
 */
const RoutedListingInner: React.FunctionComponent<RoutedListingProps> = function (props) {
    const {
        listingTitle,
        createButton,
        listingColumns,
        model,
        searchBarType,
        filters,
        filtersControl = false,
        onRowClick,
    } = props;

    const dispatch = useDispatch();
    const theme = useTheme();
    const xIdSociete = useSociete();
    const { setSearchBarType, setIsSearchBarPath } = useSearchBar();

    const { datasourcesThunks, selector, sliceName, key } = model;
    const fetchAction = datasourcesThunks.main.getData as getDataThunkType<never>;

    const modelSelector = useSelector(selector);
    const datasource = modelSelector.main;

    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);

    // Configurer le type de SearchBar et réinitialiser la recherche au montage.
    useEffect(() => {
        if (searchBarType) {
            setSearchBarType(searchBarType as any);
            setIsSearchBarPath(true);

            setTimeout(() => {
                dispatch({
                    type: `${sliceName}/set${key.charAt(0).toUpperCase() + key.slice(1)}Search`,
                    payload: { search: '' },
                });
            }, 0);
        }
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [dispatch, key, sliceName, searchBarType, setIsSearchBarPath, setSearchBarType]);

    // Fetch initial au montage.
    useEffect(() => {
        dispatch(datasourcesThunks.main.getData({}) as any);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClickRow = useCallback(
        (_: React.MouseEvent<HTMLElement>, item: SelectableItem) => {
            onRowClick(item);
        },
        [onRowClick],
    );

    const errorContent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '50vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '50%',
                        width: '60%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des données."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    {searchBarType && (
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <GenericSearchBar />
                        </Box>
                    )}
                    <ListSkeleton
                        showPreview={false}
                        columns={5}
                        rows={8}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <Box>
                <ListHeader
                    breadcrumbPath={[listingTitle]}
                    actions={
                        <HeaderActionButton
                            label={createButton.label}
                            icon={createButton.icon}
                            onClick={createButton.onClick}
                            actionType="navigation"
                        />
                    }
                />
                {datasource && (
                    <Box sx={{ width: '100%' }}>
                        <StoreDatatable
                            dataSource={datasource}
                            columns={listingColumns}
                            fetchData={fetchAction}
                            filters={filters ?? []}
                            filtersControl={filtersControl}
                            hideColumnOptions
                            onClickRow={onClickRow}
                            observers={[xIdSociete]}
                        />
                    </Box>
                )}
            </Box>
        </SkeletonLoader>
    );
};

export default RoutedListingInner;
